import { Fragment, memo, useCallback, useEffect } from 'react';
import { useFormikContext } from 'formik';
import { Typography, useEventCallback } from '@mui/material';
import Table from 'shell/components/shared/table';
import { headCells } from './constants';
import { useNavigate } from 'react-router-dom';
import { MetricData } from 'shell/models/type-metric';
import { AlertModal } from 'system/components/shared/delete-alert/types';
import {
  HttpRequestFunction,
  RequestOptionsOverride,
  ResponsePromise,
} from 'shell/hooks/use-http-fetch/stateless/type';
import { HttpBackendError } from 'shell/error/definitions/http-backend-error';
import { HttpResponseList } from 'shell/models/http-response';
import { transformDataset } from 'practice/pages/metric/utils';
import { DateTime } from 'luxon';
import {
  BodyResearchMetric,
  ResearchMetricTableVM,
  ResearchMetricVM,
} from 'practice/view-models/research-metric';

interface TableMetricProps {
  activeDelete?: boolean;
  activeEdit?: boolean;
  setDeleteAlertModal: (value: AlertModal<BodyResearchMetric>) => void;
  getMetric:
    | ((options?: RequestOptionsOverride | undefined) => Promise<Error | HttpBackendError>)
    | HttpRequestFunction<HttpResponseList<MetricData>, BodyResearchMetric[]>;
  metric: Array<BodyResearchMetric>;
}

const TableMetricComponent = ({
  activeDelete,
  activeEdit,
  getMetric,
  setDeleteAlertModal,
  metric,
}: TableMetricProps): React.ReactElement => {
  const formContext = useFormikContext<ResearchMetricVM>();

  const navigate = useNavigate();

  const onChange = useEventCallback(
    (
      key: keyof ResearchMetricTableVM,
      value: ResearchMetricTableVM[keyof ResearchMetricTableVM]
    ) => {
      formContext.setFieldValue('table', {
        ...(formContext.values.table || {}),
        [key]: value,
      });
    }
  );

  const onChanges = useEventCallback((values: ResearchMetricTableVM) => {
    formContext.setFieldValue('table', {
      ...(formContext.values.table || {}),
      ...values,
    });
  });

  const onChangePage = useEventCallback((page: number) => {
    onChange('page', page || 0);
  });

  const onChangeRowsPerPage = useEventCallback((rowsPerPage: number) => {
    onChanges({
      rowsPerPage: rowsPerPage || 0,
      page: 0,
    });
  });

  const onVisibilityPress = useEventCallback(
    (cell: BodyResearchMetric): Promise<BodyResearchMetric> => {
      return new Promise(() => {
        navigate(
          `/pratiche/ricerca-metrica/dettaglio/${cell?.register}/${cell?.practice}/${
            cell?.typeOperation
          }/${cell?.codState}/${DateTime.fromFormat(
            cell?.validityFrom || '',
            'dd/MM/yyyy'
          ).toFormat('yyyy-MM-dd')}`
        );
      });
    }
  );

  const onEditPress = useEventCallback((cell: BodyResearchMetric): Promise<BodyResearchMetric> => {
    return new Promise(() => {
      navigate(
        `/pratiche/ricerca-metrica/modifica/${cell?.register}/${cell?.practice}/${
          cell?.typeOperation
        }/${cell?.codState}/${DateTime.fromFormat(cell?.validityFrom || '', 'dd/MM/yyyy').toFormat(
          'yyyy-MM-dd'
        )}`
      );
    });
  });

  const onTrashPress = useCallback(
    (cell: BodyResearchMetric): Promise<BodyResearchMetric> => {
      return new Promise((resolve) => {
        setDeleteAlertModal({ open: true, element: cell });
        resolve(cell);
      }) as Promise<BodyResearchMetric>;
    },
    [setDeleteAlertModal]
  );

  useEffect(() => {
    if (metric?.length > 0) {
      formContext.setFieldValue('table', {
        ...formContext.values?.table,
        body: metric,
      });
    } else {
      formContext.setFieldValue('table', {
        ...formContext.values?.table,
        body: metric,
      });
    }
  }, [metric]);

  const onSort = useCallback(
    (orders) => {
      const newDataset = {
        ...formContext.values,
        filter: {
          ...formContext.values?.filter,
          orders,
        },
      };
      const queryString = transformDataset(newDataset);
      (
        getMetric({
          params: {},
          paramsSerializer: () => queryString,
        }) as ResponsePromise<HttpResponseList<MetricData>, Array<BodyResearchMetric>>
      ).then(() => {
        formContext.setFieldValue('filter', {
          ...{
            ...formContext.values?.filter,
            orders,
          },
        });
      });
    },
    [formContext.setFieldValue, getMetric, formContext.values]
  );

  return (
    <Fragment>
      {formContext.values.table?.body ? (
        (formContext.values.table?.body || [])?.length > 0 ? (
          <Table
            ariaLabel="Lista tipi metrica"
            page={formContext.values.table?.page || 0}
            rowsPerPage={formContext.values.table?.rowsPerPage}
            body={formContext.values.table?.body || []}
            count={(formContext.values.table?.body || [])?.length}
            head={headCells}
            onSort={onSort}
            onPageChange={onChangePage}
            onRowsPerPageChange={onChangeRowsPerPage}
            onVisibilityPress={onVisibilityPress}
            onEditPress={onEditPress}
            onTrashPress={onTrashPress}
            orders={formContext.values.filter?.orders}
            isNotLayoutFixed
            activeActions={{
              delete: activeDelete,
              read: true,
              edit: activeEdit,
            }}
          />
        ) : (
          <Typography color="secondary.main">
            Nessun elemento trovato con la ricerca effettuata.
          </Typography>
        )
      ) : undefined}
    </Fragment>
  );
};

TableMetricComponent.displayName = 'Table Metric component';

const TableMetric = memo(TableMetricComponent);

export default TableMetric;
